<template>
  <Breadcrumbs main="Dashboard" title="Ecommerce" />
  <div class="container-fluid dashboard-2">
    <div class="row">
      <sellCard
        :total-pedidos="metrics.current.totalPedidosActivos"
        :trend-percentage="trends.pedidosActivos"
        :trend-label="trendLabel"
      />
      <profitCard
        :total-ventas="metrics.current.totalVentas"
        :trend-percentage="trends.totalVentas"
        :trend-label="trendLabel"
      />
      <visitorCard
        :total-concluidos="metrics.current.pedidosConcluidos"
        :completion-percentage="completionPercentage"
        :completion-label="completionLabel"
      />
      <div class="col-12">
        <div class="row">
          <bestSeller />
        </div>
      </div>
      <sellingProduct />
      <productSlider />
    </div>
  </div>
</template>

<script>
import profitCard from '@/components/dashboards/e-commerce/profitCard.vue'
import visitorCard from '@/components/dashboards/e-commerce/visitorCard.vue'
import sellCard from '@/components/dashboards/e-commerce/sellCard.vue'
import bestSeller from '@/components/dashboards/e-commerce/bestSeller.vue'
import sellingProduct from '@/components/dashboards/e-commerce/sellingProduct.vue'
import productSlider from '@/components/dashboards/e-commerce/productSlider.vue'
import { getPedidosMetrics } from '@/services/pedidosService'

const METRICS_CACHE_STORAGE_KEY = 'dashboardMonthlyMetrics'
const METRICS_CACHE_TTL_MS = 60 * 60 * 1000 // 1 hora

export default {
  components: {
    profitCard,
    visitorCard,
    sellCard,
    bestSeller,
    sellingProduct,
    productSlider,
  },
  data() {
    return {
      metrics: {
        current: {
          totalPedidos: 0,
          totalPedidosActivos: 0,
          totalVentas: 0,
          pedidosConcluidos: 0,
        },
        previous: {
          totalPedidos: 0,
          totalPedidosActivos: 0,
          totalVentas: 0,
          pedidosConcluidos: 0,
        },
      },
      trends: {
        pedidosActivos: 0,
        totalVentas: 0,
      },
      trendLabel: 'Comparado con mismo periodo anterior',
      completionPercentage: 0,
      completionLabel: 'Porcentaje de pedidos concluidos del periodo',
      metricsLoading: false,
      metricsError: null,
    }
  },
  created() {
    this.loadMonthlyMetrics()
  },
  methods: {
    async loadMonthlyMetrics() {
      const currentRange = this.getCurrentMonthlyRange()
      const previousRange = this.getPreviousMonthlyRange(currentRange)
      const rangeKey = `${currentRange.startTimestamp}-${currentRange.endTimestamp}-${previousRange.startTimestamp}-${previousRange.endTimestamp}`

      const cachedMetrics = this.getCachedMetrics(rangeKey)
      if (cachedMetrics) {
        this.metrics = cachedMetrics.metrics
        this.trends = cachedMetrics.trends
        this.completionPercentage =
          typeof cachedMetrics.completionPercentage === 'number'
            ? cachedMetrics.completionPercentage
            : this.calculateCompletionPercentage(cachedMetrics.metrics.current)
        return
      }

      this.metricsLoading = true
      this.metricsError = null

      try {
        const [currentResponse, previousResponse] = await Promise.all([
          getPedidosMetrics(currentRange.startTimestamp, currentRange.endTimestamp),
          getPedidosMetrics(previousRange.startTimestamp, previousRange.endTimestamp),
        ])

        const hasCurrentError = !currentResponse?.success
        const hasPreviousError = !previousResponse?.success

        if (hasCurrentError && currentResponse?.error) {
          this.metricsError = currentResponse.error
        } else if (hasPreviousError && previousResponse?.error) {
          this.metricsError = previousResponse.error
        } else if (hasCurrentError || hasPreviousError) {
          this.metricsError = 'Error desconocido al obtener métricas de pedidos'
        }

        const currentMetrics = this.normalizeMetrics(currentResponse?.data)
        const previousMetrics = this.normalizeMetrics(previousResponse?.data)

        const trends = this.calculateTrends(currentMetrics, previousMetrics)
        const completionPercentage = this.calculateCompletionPercentage(currentMetrics)

        this.metrics = {
          current: currentMetrics,
          previous: previousMetrics,
        }
        this.trends = trends
        this.completionPercentage = completionPercentage

        this.setCachedMetrics(rangeKey, {
          metrics: this.metrics,
          trends,
          completionPercentage,
        })
      } catch (error) {
        console.error('Error loading monthly metrics:', error)
        this.metricsError = error?.message || 'Error al obtener métricas de pedidos'
      } finally {
        this.metricsLoading = false
      }
    },
    getCurrentMonthlyRange() {
      const now = new Date()

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)

      const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)

      return {
        startTimestamp: startOfMonth.getTime(),
        endTimestamp: endOfToday.getTime(),
      }
    },
    getPreviousMonthlyRange(currentRange) {
      const now = new Date()
      const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0)

      const startOfPreviousMonth = new Date(
        previousMonthDate.getFullYear(),
        previousMonthDate.getMonth(),
        1,
        0,
        0,
        0,
        0
      )

      const daysInPreviousMonth = new Date(
        previousMonthDate.getFullYear(),
        previousMonthDate.getMonth() + 1,
        0
      ).getDate()

      const targetDay = Math.min(
        Math.floor(
          (currentRange.endTimestamp - currentRange.startTimestamp) / (24 * 60 * 60 * 1000)
        ) + 1,
        daysInPreviousMonth
      )

      const endOfPreviousPeriod = new Date(
        previousMonthDate.getFullYear(),
        previousMonthDate.getMonth(),
        targetDay,
        23,
        59,
        59,
        999
      )

      return {
        startTimestamp: startOfPreviousMonth.getTime(),
        endTimestamp: endOfPreviousPeriod.getTime(),
      }
    },
    getCachedMetrics(rangeKey) {
      if (typeof window === 'undefined' || !window.localStorage) {
        return null
      }

      try {
        const cached = window.localStorage.getItem(METRICS_CACHE_STORAGE_KEY)

        if (!cached) {
          return null
        }

        const parsed = JSON.parse(cached)

        if (!parsed || parsed.rangeKey !== rangeKey || typeof parsed.cachedAt !== 'number') {
          return null
        }

        const isExpired = Date.now() - parsed.cachedAt > METRICS_CACHE_TTL_MS
        if (isExpired) {
          return null
        }

        if (!parsed.data || !parsed.data.metrics || !parsed.data.trends) {
          return null
        }

        return {
          metrics: {
            current: this.normalizeMetrics(parsed.data.metrics.current),
            previous: this.normalizeMetrics(parsed.data.metrics.previous),
          },
          trends: this.normalizeTrends(parsed.data.trends),
          completionPercentage: Number(parsed.data.completionPercentage ?? 0),
        }
      } catch (error) {
        console.warn('Error reading dashboard metrics cache:', error)
        return null
      }
    },
    setCachedMetrics(rangeKey, payload) {
      if (typeof window === 'undefined' || !window.localStorage) {
        return
      }

      try {
        const dataToStore = {
          rangeKey,
          cachedAt: Date.now(),
          data: payload,
        }

        window.localStorage.setItem(METRICS_CACHE_STORAGE_KEY, JSON.stringify(dataToStore))
      } catch (error) {
        console.warn('Error setting dashboard metrics cache:', error)
      }
    },
    normalizeMetrics(metrics = {}) {
      if (!metrics) {
        return {
          totalPedidos: 0,
          totalPedidosActivos: 0,
          totalVentas: 0,
          pedidosConcluidos: 0,
        }
      }

      return {
        totalPedidos: Number(metrics.totalPedidos) || 0,
        totalPedidosActivos: Number(metrics.totalPedidosActivos) || 0,
        totalVentas: Number(metrics.totalVentas) || 0,
        pedidosConcluidos: Number(metrics.pedidosConcluidos) || 0,
      }
    },
    normalizeTrends(trends = {}) {
      return {
        pedidosActivos: Number.isFinite(trends.pedidosActivos) ? trends.pedidosActivos : 0,
        totalVentas: Number.isFinite(trends.totalVentas) ? trends.totalVentas : 0,
      }
    },
    calculateTrends(currentMetrics, previousMetrics) {
      return {
        pedidosActivos: this.calculateTrendPercentage(
          currentMetrics.totalPedidosActivos,
          previousMetrics.totalPedidosActivos
        ),
        totalVentas: this.calculateTrendPercentage(
          currentMetrics.totalVentas,
          previousMetrics.totalVentas
        ),
      }
    },
    calculateTrendPercentage(currentValue, previousValue) {
      const currentNumber = Number(currentValue) || 0
      const previousNumber = Number(previousValue) || 0

      if (previousNumber === 0) {
        return currentNumber === 0 ? 0 : 100
      }

      const difference = currentNumber - previousNumber
      const percentage = (difference / Math.abs(previousNumber)) * 100

      return Math.round(percentage * 100) / 100
    },
    calculateCompletionPercentage(currentMetrics = {}) {
      const concluidos = Number(currentMetrics.pedidosConcluidos) || 0
      const pedidosActivos = Number(currentMetrics.totalPedidosActivos) || 0

      if (pedidosActivos === 0) {
        return 0
      }

      return Math.round((concluidos / pedidosActivos) * 10000) / 100
    },
  },
}
</script>
